import { clientNameOf, displayAddress, isInspectionLocked, type V2Inspection } from '../../domain/inspection';
import { useT, type MessageKey, type Translator } from '../../i18n/i18n';
import { propertyTypeLabel } from '../../i18n/labels';
import { buildCloseout } from '../../state/closeout';
import { liveRowFacts } from '../../state/rowFacts';
import type { FindingState } from '../../state/findingStore';
import { inspectionProgress, type InspectionState } from '../../state/inspectionStore';

// What the Home and the Inspections list both say about one inspection: its real stage (Owner task
// book 2026-09-04 Round 2 §A: Draft · In Progress · Ready / Closeout · Report Complete), the street
// line, the locality line, the client, and the inspector's initials. Owner 2026-10-20: a copy exported to
// another device is 'movedOut' — view only here, the work goes on there.
export type InspectionStage = 'draft' | 'inProgress' | 'ready' | 'reportComplete' | 'movedOut';

const STAGE_KEYS: Record<InspectionStage, MessageKey> = {
  draft: 'inspectionStage.draft',
  inProgress: 'inspectionStage.inProgress',
  ready: 'inspectionStage.ready',
  reportComplete: 'inspectionStage.reportComplete',
  movedOut: 'inspectionStage.movedOut'
};

export function inspectionStageOf(inspection: V2Inspection, state: InspectionState, findings: FindingState): InspectionStage {
  if (isInspectionLocked(inspection)) return 'reportComplete';
  if (inspection.movedOut !== null) return 'movedOut';
  const progress = inspectionProgress(state, inspection.id);
  if (progress.total > 0 && progress.notChecked === 0 && buildCloseout(inspection.id, state, findings, liveRowFacts()).canFinalize) return 'ready';
  if (inspection.status === 'draft' && progress.settled === 0) return 'draft';
  return 'inProgress';
}

export function StageBadge(props: { stage: InspectionStage }) {
  const { t } = useT();
  return (
    <span className={`badge stage-badge stage-badge-${props.stage}`} data-testid="inspection-stage" data-stage={props.stage}>
      {t(STAGE_KEYS[props.stage])}
    </span>
  );
}

// Card and row title: the street line; the whole one-line address only when no street was entered.
export function streetOf(inspection: V2Inspection, fallback: string): string {
  const street = inspection.property.address.trim();
  return street.length > 0 ? street : displayAddress(inspection, fallback);
}

// The line under the street: city, state + ZIP, then the property type once it is set. The place
// is left out when there is no street, because the title already carries it then.
export function localityOf(inspection: V2Inspection, t: Translator['t']): string {
  const p = inspection.property;
  const place =
    p.address.trim().length > 0
      ? [p.city, [p.state, p.zip].filter((s) => s.trim().length > 0).join(' ')]
          .map((s) => s.trim())
          .filter((s) => s.length > 0)
          .join(', ')
      : '';
  const type = p.propertyType !== null ? propertyTypeLabel(t, p.propertyType) : '';
  return [place, type].filter((s) => s.length > 0).join(' · ');
}

// B5 (2026-10-27): a client who signed the agreement is never 「未填客户」 — the signed name stands in (clientNameOf).
export function clientOf(inspection: V2Inspection, t: Translator['t']): string {
  const name = clientNameOf(inspection);
  return name.length > 0 ? name : t('inspection.noClient');
}

// Avatar initials from the inspector's own name: the first letters of the first two words for a
// Latin-script name, otherwise the first character.
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter((w) => w.length > 0);
  if (words.length === 0) return '';
  if (/^[A-Za-z]/.test(words[0])) return words.slice(0, 2).map((w) => w[0].toUpperCase()).join('');
  return Array.from(words[0])[0];
}
